import React, {
  memo, useCallback, useMemo, useRef, useState,
} from 'react';

import {
  func, bool, string, arrayOf, shape, number,
} from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Icon } from '@picsio/ui';
import { Product as ProductIcon } from '@picsio/icons';
import {
  ASYNC_JOB_STATUS_WAITING,
  ASYNC_JOB_STATUS_RUNNING,
} from '@picsio/db/src/constants';

import Logger from '../../../services/Logger';
import UiBlocker from '../../../services/UiBlocker';
import l10n from '../../../shared/strings';
import {
  addAssets,
  attachAssetsToTargets,
  getListings,
  getVariants,
} from '../../../store/products/actions';
import { getListingVariants } from '../../../store/products/getListingVariants';
import { getChannelEntryId } from '../../../store/products/helpers/channelHelpers';
import {
  getListedChannelEntries,
  productNeedsTargetsModal,
  selectMasterProduct,
  getChannelEntryListingProductId,
} from '../../../store/selectors/productSelectors';
import confirmListingMasterConsistencyIfNeeded, {
  CONSISTENCY_ACTIONS,
} from '../../../helpers/shopify/confirmListingMasterConsistencyIfNeeded';
import confirmShopifyPrepareIfNeeded, {
  PREPARE_ACTIONS,
} from '../../../helpers/shopify/confirmShopifyPrepareIfNeeded';
import { getShopifyListingsFromTargets } from '../../../helpers/shopify/listingMasterConsistency';
import ProductsDropdown from '../../ProductsDropdown/ProductsDropdown';
import { showAssetTargetsDialog } from '../../AssetTargetsDialog';
import ProductLinksSections from './ProductLinksSections';

const buildDropdownItems = (masterProducts) => (
  masterProducts.map(({ _id, title }) => ({
    _id,
    title,
    masterProductId: _id,
  }))
);

const Products = ({
  products, productVariants, selectedAssets, changeTree, openedTree,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const dropdownRef = useRef(null);
  const [isPreparingProduct, setIsPreparingProduct] = useState(false);

  const closeDropdown = useCallback(() => {
    dropdownRef.current?.hideDropdown?.();
  }, []);
  const aggregatedData = useSelector((state) => state.assets.selectedAssetsAggregatedData);
  const masterProducts = useSelector((state) => state.products.items);
  const teamSalesChannels = useSelector((state) => state.user.team?.salesChannels || []);
  const selectedAssetItems = useSelector((state) => state.assets.items.filter(
    (asset) => selectedAssets.includes(asset._id),
  ));
  const isDisabled = [ASYNC_JOB_STATUS_RUNNING, ASYNC_JOB_STATUS_WAITING].includes(aggregatedData.productAttaching) || aggregatedData.productDetaching;
  const isDropdownBusy = isDisabled || isPreparingProduct;

  const dropdownItems = useMemo(
    () => buildDropdownItems(masterProducts),
    [masterProducts],
  );

  const resolveMasterProduct = useCallback((item) => (
    masterProducts.find(({ _id }) => _id === item._id) || item
  ), [masterProducts]);

  const getProductFromStore = useCallback((masterId, fallback) => (
    selectMasterProduct(store.getState(), masterId) || fallback
  ), [store]);

  const ensureProductData = useCallback(async (product) => {
    const masterId = product._id;
    let current = getProductFromStore(masterId, product);
    const getProductsState = () => store.getState().products;

    if (!current.variants?.length) {
      await dispatch(getVariants({ _id: masterId }));
      current = getProductFromStore(masterId, current);
    }

    if (!getListedChannelEntries(current, getProductsState()).length) {
      await dispatch(getListings({ masterProductId: masterId }));
      current = getProductFromStore(masterId, current);
    }

    const variantLoads = getListedChannelEntries(current, getProductsState()).map((channel) => dispatch(getListingVariants({
      masterProductId: masterId,
      channelId: getChannelEntryId(channel),
      listingProductId: getChannelEntryListingProductId(channel),
    })));

    if (variantLoads.length) {
      await Promise.all(variantLoads);
      current = getProductFromStore(masterId, current);
    }

    return current;
  }, [dispatch, getProductFromStore]);

  const openAttachDialog = useCallback((product) => {
    showAssetTargetsDialog({
      product,
      productsState: store.getState().products,
      multipleAssetsSelected: selectedAssets.length > 1,
      salesChannels: teamSalesChannels,
      onConfirm: async (targets) => {
        const {
          action: consistencyAction,
          targets: nextTargets,
        } = await confirmListingMasterConsistencyIfNeeded({
          targets,
          assets: selectedAssetItems,
          masterProductId: product._id,
          productsState: store.getState().products,
          salesChannels: teamSalesChannels,
        });

        if (consistencyAction === CONSISTENCY_ACTIONS.CANCEL) {
          return;
        }

        if (getShopifyListingsFromTargets(nextTargets, teamSalesChannels).length) {
          const prepareAction = await confirmShopifyPrepareIfNeeded(selectedAssetItems);
          if (prepareAction !== PREPARE_ACTIONS.PROCEED) {
            return;
          }
        }

        dispatch(attachAssetsToTargets({
          assetIds: selectedAssets,
          masterProductId: product._id,
          targets: nextTargets,
        }));
      },
    });
  }, [
    dispatch,
    selectedAssetItems,
    selectedAssets,
    teamSalesChannels,
    store,
  ]);

  const addToProduct = useCallback(async (item) => {
    Logger.log('User', 'InfoPanelAddToProduct', { productId: item._id });
    closeDropdown();
    setIsPreparingProduct(true);
    UiBlocker.block(l10n.DIALOGS.ASSET_TARGETS.LOADING_PRODUCT);

    let product;
    try {
      product = await ensureProductData(resolveMasterProduct(item));
    } catch (error) {
      Logger.error(new Error('Failed to prepare product for attach'), { error }, [
        'InfoPanelPrepareProductFailed',
        { productId: item._id },
      ]);
      return;
    } finally {
      UiBlocker.unblock();
      setIsPreparingProduct(false);
    }

    if (!productNeedsTargetsModal(product, store.getState().products)) {
      dispatch(addAssets({ assetIds: selectedAssets, product }));
      return;
    }

    openAttachDialog(product);
  }, [
    closeDropdown,
    dispatch,
    ensureProductData,
    openAttachDialog,
    resolveMasterProduct,
    selectedAssets,
  ]);

  const icon = useCallback(() => (
    <Icon>
      <ProductIcon />
    </Icon>
  ), []);

  return (
    <>
      <ProductsDropdown
        ref={dropdownRef}
        placeholder="Products"
        placeholderIcon="placeholderProduct"
        icon={icon}
        filterText="Choose Products"
        checkedItems={[]}
        showChips={false}
        items={dropdownItems}
        canCreate={false}
        createHandler={null}
        readOnly={false}
        disabled={isDropdownBusy}
        inProgress={isDropdownBusy}
        onCheckedHandler={addToProduct}
      />
      <ProductLinksSections
        products={products}
        productVariants={productVariants}
        selectedAssets={selectedAssets}
        selectedAssetItems={selectedAssetItems}
        disabled={isDisabled}
        changeTree={changeTree}
        openedTree={openedTree}
      />
    </>
  );
};

Products.propTypes = {
  products: arrayOf(shape({
    _id: string,
    title: string,
    type: string,
    salesChannel: string,
    isDraft: bool,
  })),
  productVariants: arrayOf(shape({
    variantId: string,
    productId: string,
    title: string,
    type: string,
    numberOfAssets: number,
    isDraft: bool,
  })),
  lightboardsEditable: bool,
  selectedAssets: arrayOf(string),
  changeTree: func,
  remove: func,
  openedTree: string,
  disabled: bool,
};

export default memo(Products);
